const DB_NAME = 'doddledays-offline';
const DB_VERSION = 3;
const SNAPSHOT_STORE = 'snapshots';
const TRIP_STORE = 'trips';
const META_STORE = 'meta';
const RESILIENCE_STORE = 'resilience';
const GLOBAL_SLUG = '__global__';
const OFFLINE_MAP_CACHE = 'doddledays-offline-map-v1';
const OFFLINE_MEDIA_CACHE = 'doddledays-offline-media-v1';
const RESILIENCE_MAP_CACHE = 'doddledays-resilience-map-v1';
const OFFLINE_MAP_MARKER = '/__doddledays-plus-map-cache-enabled__';
const OFFLINE_MEDIA_PATH = '/__doddledays-offline-media/';
const RESILIENCE_MEDIA_META = 'resilience-media-urls';
const pendingResilienceOverviews = new Map<string, unknown>();

type OfflineTripRecord = {
  slug: string;
  state: 'preparing' | 'ready';
  updated_at: string | null;
  media_urls: string[];
};

type OfflineSnapshotRecord = {
  key: string;
  slug: string;
  resource: string;
  value: unknown;
  bytes: number;
  updated_at: string;
};

type OfflineMetaRecord = {
  key: string;
  value: unknown;
  updated_at: string;
};

type ResilienceContext = {
  slug: string;
  routeId: string;
  documentSlugs: string[];
};

export type OfflineMediaEntry = {
  storageKey: string;
  blob?: Blob;
};

export type OfflineTripStatus = {
  supported: boolean;
  entitled: boolean;
  enabled: boolean;
  updating: boolean;
  updated_at: string | null;
  cached_bytes: number;
  persistent: boolean | null;
};

export function offlineTripRecordIsAvailable(record: Pick<OfflineTripRecord, 'state' | 'updated_at'> | undefined): boolean {
  return record?.state === 'ready' || (record?.state === 'preparing' && Boolean(record.updated_at));
}

function storageSupported(): boolean {
  return typeof indexedDB !== 'undefined';
}

function requestValue<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Offline storage request failed'));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error('Offline storage transaction failed'));
    transaction.onabort = () => reject(transaction.error ?? new Error('Offline storage transaction aborted'));
  });
}

function openDb(): Promise<IDBDatabase> {
  if (!storageSupported()) return Promise.reject(new Error('Offline storage is not supported by this browser'));
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = request.result;
      if (!db.objectStoreNames.contains(SNAPSHOT_STORE)) {
        const snapshots = db.createObjectStore(SNAPSHOT_STORE, { keyPath: 'key' });
        snapshots.createIndex('slug', 'slug', { unique: false });
      }
      if (!db.objectStoreNames.contains(TRIP_STORE)) {
        db.createObjectStore(TRIP_STORE, { keyPath: 'slug' });
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(RESILIENCE_STORE)) {
        const resilience = db.createObjectStore(RESILIENCE_STORE, { keyPath: 'key' });
        resilience.createIndex('slug', 'slug', { unique: false });
      }
      if (event.oldVersion < 3 && db.objectStoreNames.contains(RESILIENCE_STORE)) {
        request.transaction?.objectStore(RESILIENCE_STORE).clear();
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Could not open offline storage'));
  });
}

async function getTripRecord(slug: string): Promise<OfflineTripRecord | undefined> {
  if (!storageSupported()) return undefined;
  const db = await openDb();
  try {
    const tx = db.transaction(TRIP_STORE, 'readonly');
    return (await requestValue(tx.objectStore(TRIP_STORE).get(slug))) as OfflineTripRecord | undefined;
  } finally {
    db.close();
  }
}

async function putTripRecord(record: OfflineTripRecord): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction(TRIP_STORE, 'readwrite');
    tx.objectStore(TRIP_STORE).put(record);
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

async function getMeta<T>(key: string): Promise<T | undefined> {
  if (!storageSupported()) return undefined;
  const db = await openDb();
  try {
    const tx = db.transaction(META_STORE, 'readonly');
    const record = (await requestValue(tx.objectStore(META_STORE).get(key))) as OfflineMetaRecord | undefined;
    return record?.value as T | undefined;
  } finally {
    db.close();
  }
}

async function putMeta(key: string, value: unknown): Promise<void> {
  if (!storageSupported()) return;
  const db = await openDb();
  try {
    const tx = db.transaction(META_STORE, 'readwrite');
    tx.objectStore(META_STORE).put({ key, value, updated_at: new Date().toISOString() } satisfies OfflineMetaRecord);
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

async function postServiceWorkerMessage(message: unknown): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  try {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage(message);
      return;
    }
    const registration = await navigator.serviceWorker.ready;
    registration.active?.postMessage(message);
  } catch {
    // Offline storage must continue to work even when service-worker messaging is unavailable.
  }
}

async function removeCachedUrls(urls: string[]): Promise<void> {
  if (typeof caches === 'undefined' || urls.length === 0) return;
  const cache = await caches.open(OFFLINE_MEDIA_CACHE);
  await Promise.all(urls.map((url) => {
    const resolved = typeof window !== 'undefined' ? new URL(url, window.location.origin).toString() : url;
    return cache.delete(resolved);
  }));
}

export function offlineMediaUrl(storageKey: string): string {
  return `${OFFLINE_MEDIA_PATH}${encodeURIComponent(storageKey)}`;
}

function offlineMediaRequest(storageKey: string): Request {
  const url = typeof window !== 'undefined'
    ? new URL(offlineMediaUrl(storageKey), window.location.origin).toString()
    : offlineMediaUrl(storageKey);
  return new Request(url);
}

export async function hasOfflineMedia(storageKey: string): Promise<boolean> {
  if (typeof caches === 'undefined') return false;
  const cache = await caches.open(OFFLINE_MEDIA_CACHE);
  return Boolean(await cache.match(offlineMediaRequest(storageKey)));
}

async function putOfflineMediaEntries(entries: OfflineMediaEntry[]): Promise<string[]> {
  if (typeof caches === 'undefined') throw new Error('Offline media storage is not supported by this browser');
  const cache = await caches.open(OFFLINE_MEDIA_CACHE);
  const nextUrls = Array.from(new Set(entries.map((entry) => offlineMediaUrl(entry.storageKey))));
  for (const entry of entries) {
    const request = offlineMediaRequest(entry.storageKey);
    if (entry.blob) {
      const headers = new Headers();
      if (entry.blob.type) headers.set('Content-Type', entry.blob.type);
      await cache.put(request, new Response(entry.blob, { headers }));
      continue;
    }
    if (!(await cache.match(request))) {
      throw new Error(`Offline media is missing: ${entry.storageKey}`);
    }
  }
  return nextUrls;
}

export async function cacheOfflineMediaFiles(slug: string, entries: OfflineMediaEntry[]): Promise<void> {
  if (!(await hasOfflineEntitlement())) return;
  const record = await getTripRecord(slug);
  if (!record) return;
  const nextUrls = await putOfflineMediaEntries(entries);
  const removedUrls = record.media_urls.filter((url) => !nextUrls.includes(url));
  if (removedUrls.length) await removeCachedUrls(removedUrls);
  await putTripRecord({ ...record, media_urls: nextUrls });
}

export async function cacheResilienceMediaFiles(entries: OfflineMediaEntry[]): Promise<void> {
  if (!storageSupported()) return;
  const owner = await getMeta<string | null>('cache-owner');
  if (!owner) return;
  const nextUrls = await putOfflineMediaEntries(entries);
  const previousUrls = await getMeta<string[]>(RESILIENCE_MEDIA_META) ?? [];
  const removedUrls = previousUrls.filter((url) => !nextUrls.includes(url));
  if (removedUrls.length) await removeCachedUrls(removedUrls);
  await putMeta(RESILIENCE_MEDIA_META, nextUrls);
}

async function deleteSnapshotsForSlug(slug: string): Promise<void> {
  if (!storageSupported()) return;
  const db = await openDb();
  try {
    const tx = db.transaction(SNAPSHOT_STORE, 'readwrite');
    const index = tx.objectStore(SNAPSHOT_STORE).index('slug');
    const request = index.openCursor(IDBKeyRange.only(slug));
    await new Promise<void>((resolve, reject) => {
      request.onerror = () => reject(request.error ?? new Error('Could not remove offline snapshots'));
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) {
          resolve();
          return;
        }
        cursor.delete();
        cursor.continue();
      };
    });
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

async function clearPlusOfflineContent(): Promise<void> {
  if (storageSupported()) {
    const db = await openDb();
    try {
      const tx = db.transaction([SNAPSHOT_STORE, TRIP_STORE], 'readwrite');
      tx.objectStore(SNAPSHOT_STORE).clear();
      tx.objectStore(TRIP_STORE).clear();
      await transactionDone(tx);
    } finally {
      db.close();
    }
  }
  if (typeof caches !== 'undefined') {
    await Promise.all([caches.delete(OFFLINE_MAP_CACHE), caches.delete(OFFLINE_MEDIA_CACHE)]);
  }
  await postServiceWorkerMessage({ type: 'CLEAR_OFFLINE_CACHES' });
}

async function clearResilienceContent(): Promise<void> {
  pendingResilienceOverviews.clear();
  const resilienceMediaUrls = await getMeta<string[]>(RESILIENCE_MEDIA_META) ?? [];
  if (resilienceMediaUrls.length) await removeCachedUrls(resilienceMediaUrls);
  if (storageSupported()) {
    const db = await openDb();
    try {
      const tx = db.transaction(RESILIENCE_STORE, 'readwrite');
      tx.objectStore(RESILIENCE_STORE).clear();
      await transactionDone(tx);
    } finally {
      db.close();
    }
    await putMeta('resilience-trip', null);
    await putMeta('resilience-context', null);
    await putMeta(RESILIENCE_MEDIA_META, []);
  }
  if (typeof caches !== 'undefined') await caches.delete(RESILIENCE_MAP_CACHE);
}

async function clearAllCachedContent(): Promise<void> {
  await clearPlusOfflineContent();
  await clearResilienceContent();
}

export async function setOfflineCacheIdentity(userId: string | null): Promise<void> {
  if (!storageSupported()) return;
  const currentOwner = await getMeta<string | null>('cache-owner');
  if (!userId) {
    await clearAllCachedContent();
    await putMeta('plus-entitlement', false);
    await putMeta('cache-owner', null);
    return;
  }
  const hasUnownedContent = currentOwner === undefined
    && ((await readyOfflineTripSlugs()).length > 0 || Boolean(await getMeta<string>('resilience-trip')));
  if (hasUnownedContent || (currentOwner !== undefined && currentOwner !== null && currentOwner !== userId)) {
    await clearAllCachedContent();
    await putMeta('plus-entitlement', false);
  }
  await putMeta('cache-owner', userId);
}

export async function setOfflineEntitlement(active: boolean): Promise<void> {
  if (!storageSupported()) return;
  if (!active) await clearPlusOfflineContent();
  await putMeta('plus-entitlement', active);
}

export async function hasOfflineEntitlement(): Promise<boolean> {
  const [active, owner] = await Promise.all([
    getMeta<boolean>('plus-entitlement'),
    getMeta<string | null>('cache-owner'),
  ]);
  return active === true && typeof owner === 'string' && owner.length > 0;
}

export async function beginOfflineTrip(slug: string): Promise<void> {
  if (!storageSupported()) throw new Error('Offline storage is not supported by this browser');
  if (!(await hasOfflineEntitlement())) throw new Error('Doddledays Plus is required for offline trips');
  const existing = await getTripRecord(slug);
  await putTripRecord({
    slug,
    state: 'preparing',
    updated_at: existing?.updated_at ?? null,
    media_urls: existing?.media_urls ?? [],
  });
}

export async function completeOfflineTrip(slug: string): Promise<void> {
  const existing = await getTripRecord(slug);
  if (!existing) throw new Error('Offline trip download was not started');
  await putTripRecord({ ...existing, state: 'ready', updated_at: new Date().toISOString() });
  await syncOfflineServiceWorkerState();
}

export async function cancelOfflineTripUpdate(slug: string): Promise<void> {
  const existing = await getTripRecord(slug);
  if (!existing) return;
  if (existing.updated_at) {
    await putTripRecord({ ...existing, state: 'ready' });
    await syncOfflineServiceWorkerState();
    return;
  }
  await removeOfflineTrip(slug);
}

export async function removeOfflineTrip(slug: string): Promise<void> {
  if (!storageSupported()) return;
  const existing = await getTripRecord(slug);
  if (existing?.media_urls.length) {
    await removeCachedUrls(existing.media_urls);
    await postServiceWorkerMessage({ type: 'REMOVE_OFFLINE_URLS', urls: existing.media_urls });
  }
  await deleteSnapshotsForSlug(slug);
  const db = await openDb();
  try {
    const tx = db.transaction(TRIP_STORE, 'readwrite');
    tx.objectStore(TRIP_STORE).delete(slug);
    await transactionDone(tx);
  } finally {
    db.close();
  }
  await syncOfflineServiceWorkerState();
}

function snapshotKey(slug: string, resource: string): string {
  return `${slug}::${resource}`;
}

async function writeSnapshot(slug: string, resource: string, value: unknown): Promise<void> {
  if (!storageSupported() || !(await hasOfflineEntitlement())) return;
  if (slug !== GLOBAL_SLUG && !(await getTripRecord(slug))) return;
  const serialized = JSON.stringify(value);
  const record: OfflineSnapshotRecord = {
    key: snapshotKey(slug, resource),
    slug,
    resource,
    value,
    bytes: new Blob([serialized]).size,
    updated_at: new Date().toISOString(),
  };
  const db = await openDb();
  try {
    const tx = db.transaction(SNAPSHOT_STORE, 'readwrite');
    tx.objectStore(SNAPSHOT_STORE).put(record);
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

async function readSnapshot<T>(slug: string, resource: string): Promise<T | undefined> {
  if (!storageSupported()) return undefined;
  const db = await openDb();
  try {
    const tx = db.transaction(SNAPSHOT_STORE, 'readonly');
    const record = (await requestValue(tx.objectStore(SNAPSHOT_STORE).get(snapshotKey(slug, resource)))) as OfflineSnapshotRecord | undefined;
    return record?.value as T | undefined;
  } finally {
    db.close();
  }
}

async function putResilienceSnapshot(slug: string, resource: string, value: unknown): Promise<void> {
  const serialized = JSON.stringify(value);
  const record: OfflineSnapshotRecord = {
    key: snapshotKey(slug, resource),
    slug,
    resource,
    value,
    bytes: new Blob([serialized]).size,
    updated_at: new Date().toISOString(),
  };
  const db = await openDb();
  try {
    const tx = db.transaction(RESILIENCE_STORE, 'readwrite');
    tx.objectStore(RESILIENCE_STORE).put(record);
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

async function deleteResilienceSnapshot(slug: string, resource: string): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction(RESILIENCE_STORE, 'readwrite');
    tx.objectStore(RESILIENCE_STORE).delete(snapshotKey(slug, resource));
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

function currentRoutePlannerValue(value: unknown): { value: unknown; routeId: string; documentSlugs: string[] } | null {
  if (!value || typeof value !== 'object') return null;
  const wrapper = value as { payload?: unknown };
  if (!wrapper.payload || typeof wrapper.payload !== 'object') return null;
  const payload = wrapper.payload as {
    routes?: unknown[];
    selectedRouteId?: string;
    selectedWaypointId?: string;
    checklists?: unknown;
  };
  if (!Array.isArray(payload.routes) || payload.routes.length === 0) return null;
  const routes = payload.routes as Array<{
    id?: string;
    waypoints?: Array<{ id?: string; documentAssignments?: Array<{ documentSlug?: string }> }>;
  }>;
  const selected = routes.find((route) => route.id === payload.selectedRouteId) ?? routes[0];
  if (!selected?.id) return null;
  const waypoints = Array.isArray(selected.waypoints) ? selected.waypoints : [];
  const selectedWaypointId = waypoints.some((waypoint) => waypoint.id === payload.selectedWaypointId)
    ? payload.selectedWaypointId ?? ''
    : waypoints[0]?.id ?? '';
  const documentSlugs = Array.from(new Set(
    waypoints.flatMap((waypoint) => Array.isArray(waypoint.documentAssignments)
      ? waypoint.documentAssignments.map((assignment) => assignment.documentSlug).filter((slug): slug is string => typeof slug === 'string' && slug.length > 0)
      : []),
  ));
  const nextPayload = {
    ...payload,
    routes: [selected],
    selectedRouteId: selected.id,
    selectedWaypointId,
  };
  delete nextPayload.checklists;
  return { value: { ...wrapper, payload: nextPayload }, routeId: selected.id, documentSlugs };
}

async function resilienceValue(slug: string, resource: string, value: unknown): Promise<unknown> {
  if (resource === 'planner-state') {
    const current = currentRoutePlannerValue(value);
    if (!current) return value;
    const previous = await getMeta<ResilienceContext>('resilience-context');
    if (previous?.slug === slug && previous.routeId !== current.routeId && typeof caches !== 'undefined') {
      await caches.delete(RESILIENCE_MAP_CACHE);
    }
    await putMeta('resilience-context', {
      slug,
      routeId: current.routeId,
      documentSlugs: current.documentSlugs,
    } satisfies ResilienceContext);
    return current.value;
  }

  if (resource === 'overview' && value && typeof value === 'object') {
    const context = await getMeta<ResilienceContext>('resilience-context');
    const overview = value as { documents?: Array<{ slug?: string }> };
    if (Array.isArray(overview.documents)) {
      if (context?.slug === slug) {
        const allowed = new Set(context.documentSlugs);
        return { ...overview, documents: overview.documents.filter((document) => typeof document.slug === 'string' && allowed.has(document.slug)) };
      }
      return { ...overview, documents: [] };
    }
  }

  if (resource === 'sessions' && Array.isArray(value)) return value.slice(0, 1);
  return value;
}

async function writeResilienceSnapshot(slug: string, resource: string, value: unknown): Promise<void> {
  if (!storageSupported()) return;
  const owner = await getMeta<string | null>('cache-owner');
  if (!owner) return;

  if (resource === 'overview') pendingResilienceOverviews.set(slug, value);

  const normalized = await resilienceValue(slug, resource, value);
  await putResilienceSnapshot(slug, resource, normalized);

  if (resource === 'planner-state') {
    const overview = pendingResilienceOverviews.get(slug);
    if (overview !== undefined) {
      await putResilienceSnapshot(slug, 'overview', await resilienceValue(slug, 'overview', overview));
      pendingResilienceOverviews.delete(slug);
    }
    await deleteResilienceSnapshot(slug, 'project-locations');
  }
}

function sameDocumentSlugs(left: string[], right: string[]): boolean {
  if (left.length !== right.length) return false;
  const rightSet = new Set(right);
  return left.every((slug) => rightSet.has(slug));
}

export async function cacheCurrentRouteResilience(slug: string, plannerState: unknown): Promise<boolean> {
  if (!storageSupported()) return false;
  const current = currentRoutePlannerValue(plannerState);
  const previous = await getMeta<ResilienceContext>('resilience-context');
  const contextChanged = current !== null && (
    !previous
    || previous.slug !== slug
    || previous.routeId !== current.routeId
    || !sameDocumentSlugs(previous.documentSlugs, current.documentSlugs)
  );
  await activateResilienceTrip(slug);
  await writeResilienceSnapshot(slug, 'planner-state', plannerState);
  return contextChanged;
}

export async function readResilienceDocumentSlugs(slug: string): Promise<string[]> {
  const context = await getMeta<ResilienceContext>('resilience-context');
  return context?.slug === slug ? context.documentSlugs : [];
}

async function readResilienceSnapshot<T>(slug: string, resource: string): Promise<T | undefined> {
  if (!storageSupported()) return undefined;
  const db = await openDb();
  try {
    const tx = db.transaction(RESILIENCE_STORE, 'readonly');
    const record = (await requestValue(tx.objectStore(RESILIENCE_STORE).get(snapshotKey(slug, resource)))) as OfflineSnapshotRecord | undefined;
    return record?.value as T | undefined;
  } finally {
    db.close();
  }
}

async function activateResilienceTrip(slug: string): Promise<void> {
  const current = await getMeta<string | null>('resilience-trip');
  if (current && current !== slug) {
    const db = await openDb();
    try {
      const tx = db.transaction(RESILIENCE_STORE, 'readwrite');
      tx.objectStore(RESILIENCE_STORE).clear();
      await transactionDone(tx);
    } finally {
      db.close();
    }
  }
  await putMeta('resilience-trip', slug);
}

export async function readThroughResilienceSnapshot<T>(
  slug: string,
  resource: string,
  loader: () => Promise<T>,
  activateOnSuccess = false,
): Promise<T> {
  try {
    const value = await loader();
    if (activateOnSuccess) await activateResilienceTrip(slug);
    const activeSlug = await getMeta<string | null>('resilience-trip');
    if (activeSlug === slug) await writeResilienceSnapshot(slug, resource, value);
    return value;
  } catch (error) {
    if (!isOfflineFallbackError(error)) throw error;
    const activeSlug = await getMeta<string | null>('resilience-trip');
    if (activeSlug !== slug) throw error;
    const cached = await readResilienceSnapshot<T>(slug, resource);
    if (cached === undefined) throw error;
    return cached;
  }
}

export function isOfflineFallbackError(error: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (error instanceof TypeError) return true;
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /failed to fetch|network(?:error)?|load failed|connection|offline/i.test(message);
}

function missingOptionalOfflineResource<T>(resource: string): T | undefined {
  // Older Plus downloads may predate newer optional resources. Keep the core trip usable
  // and let the next online background sync backfill the real value.
  if (resource === 'project-locations' || resource === 'sessions' || resource.startsWith('session-locations:') || resource.startsWith('evidence:')) return [] as T;
  return undefined;
}

export async function readThroughOfflineSnapshot<T>(
  slug: string,
  resource: string,
  loader: () => Promise<T>,
): Promise<T> {
  const definitelyOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (definitelyOffline) {
    const record = await getTripRecord(slug);
    if (record?.state === 'ready') {
      const cached = await readSnapshot<T>(slug, resource);
      if (cached !== undefined) return cached;
      const optionalFallback = missingOptionalOfflineResource<T>(resource);
      if (optionalFallback !== undefined) return optionalFallback;
    }
    throw new Error('This trip needs one online refresh before all offline data is available.');
  }

  try {
    const value = await loader();
    const record = await getTripRecord(slug);
    if (record && (await hasOfflineEntitlement())) await writeSnapshot(slug, resource, value);
    return value;
  } catch (error) {
    const record = await getTripRecord(slug);
    if (!record || record.state !== 'ready' || !isOfflineFallbackError(error)) throw error;
    const cached = await readSnapshot<T>(slug, resource);
    if (cached !== undefined) return cached;
    const optionalFallback = missingOptionalOfflineResource<T>(resource);
    if (optionalFallback !== undefined) return optionalFallback;
    throw error;
  }
}

export async function writeOfflineProjectIndex(value: unknown): Promise<void> {
  if (!(await hasOfflineEntitlement()) || (await readyOfflineTripSlugs()).length === 0) return;
  await writeSnapshot(GLOBAL_SLUG, 'trips', value);
}

export async function readOfflineProjectIndex<T>(): Promise<T | undefined> {
  if ((await readyOfflineTripSlugs()).length === 0) return undefined;
  return readSnapshot<T>(GLOBAL_SLUG, 'trips');
}

export async function readyOfflineTripSlugs(): Promise<string[]> {
  if (!storageSupported()) return [];
  const db = await openDb();
  try {
    const tx = db.transaction(TRIP_STORE, 'readonly');
    const records = (await requestValue(tx.objectStore(TRIP_STORE).getAll())) as OfflineTripRecord[];
    return records.filter((record) => record.state === 'ready').map((record) => record.slug);
  } finally {
    db.close();
  }
}

export async function requestPersistentOfflineStorage(): Promise<boolean | null> {
  if (typeof navigator === 'undefined' || !navigator.storage?.persist) return null;
  try {
    return await navigator.storage.persist();
  } catch {
    return null;
  }
}

export async function getOfflineTripStatus(slug: string): Promise<OfflineTripStatus> {
  if (!storageSupported()) {
    return { supported: false, entitled: false, enabled: false, updating: false, updated_at: null, cached_bytes: 0, persistent: null };
  }
  const [record, entitled] = await Promise.all([getTripRecord(slug), hasOfflineEntitlement()]);
  let cachedBytes = 0;
  const db = await openDb();
  try {
    const tx = db.transaction(SNAPSHOT_STORE, 'readonly');
    const snapshots = (await requestValue(tx.objectStore(SNAPSHOT_STORE).index('slug').getAll(IDBKeyRange.only(slug)))) as OfflineSnapshotRecord[];
    cachedBytes = snapshots.reduce((sum, snapshot) => sum + snapshot.bytes, 0);
  } finally {
    db.close();
  }
  let persistent: boolean | null = null;
  if (typeof navigator !== 'undefined' && navigator.storage?.persisted) {
    try {
      persistent = await navigator.storage.persisted();
    } catch {
      persistent = null;
    }
  }
  return {
    supported: true,
    entitled,
    enabled: offlineTripRecordIsAvailable(record),
    updating: record?.state === 'preparing',
    updated_at: record?.updated_at ?? null,
    cached_bytes: cachedBytes,
    persistent,
  };
}

export async function syncOfflineServiceWorkerState(registration?: ServiceWorkerRegistration): Promise<void> {
  const enabled = (await readyOfflineTripSlugs()).length > 0;
  if (typeof caches !== 'undefined') {
    if (enabled) {
      const cache = await caches.open(OFFLINE_MAP_CACHE);
      await cache.put(OFFLINE_MAP_MARKER, new Response('1'));
    } else {
      await caches.delete(OFFLINE_MAP_CACHE);
    }
  }
  if (registration?.active) {
    registration.active.postMessage({ type: 'SET_OFFLINE_MAP_CACHE', enabled });
    return;
  }
  await postServiceWorkerMessage({ type: 'SET_OFFLINE_MAP_CACHE', enabled });
}
